import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, imageUrl } from "../lib/api";
import {
    ChevronLeft,
    ChevronRight,
    Home,
    ZoomIn,
    ZoomOut,
    Maximize2,
    Loader2,
} from "lucide-react";
import SharePageDialog from "../components/SharePageDialog";

export default function PageViewer() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [page, setPage] = useState(null);
    const [neighbours, setNeighbours] = useState({ previous: null, next: null });
    const [loading, setLoading] = useState(true);
    const [zoom, setZoom] = useState(1);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: p }, { data: n }] = await Promise.all([
                api.get(`/pages/${id}`),
                api.get(`/pages/${id}/neighbours`),
            ]);
            setPage(p);
            setNeighbours(n);
            setZoom(1);
        } catch (e) {
            console.error(e);
            setPage(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
        // scroll viewer to top
        const el = document.getElementById("viewer-scroll");
        if (el) el.scrollTop = 0;
    }, [load]);

    // Keyboard navigation
    useEffect(() => {
        const handler = (e) => {
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
            if (e.key === "ArrowLeft" && neighbours.previous) {
                navigate(`/p/${neighbours.previous.id}`);
            } else if (e.key === "ArrowRight" && neighbours.next) {
                navigate(`/p/${neighbours.next.id}`);
            } else if (e.key === "Escape") {
                navigate("/");
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [neighbours, navigate]);

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center bg-medical-200" data-testid="viewer-loading">
                <Loader2 className="h-6 w-6 animate-spin text-teal-700" />
            </div>
        );
    }
    if (!page) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center bg-medical-200 text-slate-600" data-testid="viewer-error">
                <p className="text-sm mb-3">Page not found.</p>
                <Link to="/" className="text-teal-700 hover:underline text-sm">
                    Back to table of contents
                </Link>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col overflow-hidden" data-testid="page-viewer">
            {/* Breadcrumb / title bar */}
            <div className="flex-shrink-0 border-b border-slate-200 bg-white px-4 sm:px-8 py-3 flex items-center gap-3">
                <Link
                    to="/"
                    data-testid="viewer-home-button"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-teal-700 transition-colors"
                >
                    <Home className="h-3.5 w-3.5" />
                    Contents
                </Link>
                <ChevronRight className="h-3 w-3 text-slate-300" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded">
                    {page.category}
                </span>
                <span className="flex-1 truncate font-display font-semibold text-medical-900" data-testid="viewer-title">
                    {page.title}
                </span>
                <SharePageDialog page={page} />
                <span className="hidden sm:inline-block text-[10px] font-mono text-slate-400">
                    p.{String(page.page_number).padStart(3, "0")}
                </span>
            </div>

            {/* Viewer canvas */}
            <div
                id="viewer-scroll"
                className="flex-1 overflow-auto bg-medical-200 p-4 sm:p-8 flex flex-col items-center scroll-thin"
            >
                <div
                    className="bg-white shadow-xl rounded-sm transition-[width] duration-200"
                    style={{
                        width: `min(100%, ${Math.round(960 * zoom)}px)`,
                    }}
                >
                    <img
                        data-testid="viewer-image"
                        src={imageUrl(page.image_filename)}
                        alt={page.title}
                        className="w-full h-auto block select-none"
                        draggable={false}
                    />
                </div>
            </div>

            {/* Bottom controls */}
            <div
                data-testid="viewer-controls"
                className="flex-shrink-0 border-t border-slate-200 bg-white/95 backdrop-blur px-4 sm:px-6 py-2.5 pr-32 sm:pr-40 flex items-center justify-between gap-3 relative z-20"
            >
                <button
                    data-testid="viewer-prev-button"
                    disabled={!neighbours.previous}
                    onClick={() => neighbours.previous && navigate(`/p/${neighbours.previous.id}`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-medical-900 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Previous</span>
                </button>

                <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
                    <button
                        data-testid="viewer-zoom-out"
                        onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
                        className="p-1.5 rounded-full hover:bg-white text-medical-800 transition-colors"
                    >
                        <ZoomOut className="h-3.5 w-3.5" />
                    </button>
                    <button
                        data-testid="viewer-zoom-reset"
                        onClick={() => setZoom(1)}
                        className="px-2 py-1 text-[10px] font-mono text-medical-800 hover:bg-white rounded-full transition-colors min-w-[3rem]"
                    >
                        {Math.round(zoom * 100)}%
                    </button>
                    <button
                        data-testid="viewer-zoom-in"
                        onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.1).toFixed(2)))}
                        className="p-1.5 rounded-full hover:bg-white text-medical-800 transition-colors"
                    >
                        <ZoomIn className="h-3.5 w-3.5" />
                    </button>
                    <a
                        data-testid="viewer-open-fullsize"
                        href={imageUrl(page.image_filename)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded-full hover:bg-white text-medical-800 transition-colors"
                        title="Open image in new tab"
                    >
                        <Maximize2 className="h-3.5 w-3.5" />
                    </a>
                </div>

                <button
                    data-testid="viewer-next-button"
                    disabled={!neighbours.next}
                    onClick={() => neighbours.next && navigate(`/p/${neighbours.next.id}`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-medical-900 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}
